import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import { plansAPI } from '../../api/requests/plans';
import BigLoader from '../Skeleton/BigLoader';
import { useNavigate } from 'react-router-dom';
import { handleAlert } from '../../utils/handleAlert';
import Tick_icon from '../../assets/LandingPage/Plans/Tick_icon.svg';

const PricingTab = () => {
  const isLoggedIn = localStorage.getItem('Token');
  const navigate = useNavigate();

  const [freePlan, setFreePlan] = useState([]);
  const [loading, setLoading] = useState(false);

  const customPlan = {
    name: 'Custom Plan',
    description:
      'A flexible, high-capacity plan tailored to the needs of institutions and growing organizations.',
    price: "Let's Talk",
    button_text: 'CONTACT US',
    data: [
      'Everything in Free, plus more — tailored to your needs',
      'Scalable course storage',
      'Flexible organization size',
      'Higher contest limits and frequency',
      'Priority support and onboarding',
      'Custom integrations, workflows, and analytics',
      'Designed for institutions and growing teams',
    ],
  };

  useEffect(() => {
    setLoading(true);
    plansAPI
      .getPublicPlans()
      .then((response) => {
        const plans = response?.plans || [];

        if (plans.length > 0) {
          const zeroPricedPlan = plans.find((p) => p.price === 0);

          const formattedPlan = {
            name: zeroPricedPlan.name,
            description: zeroPricedPlan.description,
            price: '₹0',
            button_text: 'GET STARTED NOW',
            data: JSON.parse(zeroPricedPlan.data),
          };

          setFreePlan(formattedPlan);
        } else {
          handleAlert('No plans found in the response.', 'warning');
        }
      })
      .catch(() => {
        handleAlert('Something went wrong! Please try again later.', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return loading ? (
    <BigLoader />
  ) : (
    <Box
      sx={{
        backgroundColor: 'var(--color-bg)',
        padding: '50px 0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          margin: { md: 0, xs: 3 },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontSize: { md: '40px', xs: '25px' },
            fontWeight: '700',
            lineHeight: '51px',
            color: 'var(--color-text)',
          }}
        >
          Find the Right Plan and
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontSize: { md: '40px', xs: '25px' },
            fontWeight: '700',
            lineHeight: '51px',
            color: 'var(--color-primary)',
            pb: 1,
          }}
        >
          Get Started Today
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontSize: { md: '20px', xs: '17px' },
            fontWeight: '400',
            color: 'var(--color-text)',
            pb: 2,
          }}
        >
          Choose a plan that's right for you
        </Typography>
      </Box>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: { md: 'row', xs: 'column' },
          gap: 3,
          justifyContent: 'center',
          margin: { md: '25px 0 0 0', xs: '25px 25px 0 25px' },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            borderRadius: '12px',
            padding: '32px 24px',
            backgroundColor: 'var(--color-bg-secondary)',
            boxShadow: 'none',
            maxWidth: '330px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '22px',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
                pb: 1,
              }}
            >
              {freePlan?.name}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: '400',
              color: 'var(--color-text-secondary)',
              pb: 1,
            }}
          >
            {freePlan?.description}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '48px',
                fontWeight: '600',
                color: 'var(--color-text-secondary)',
              }}
            >
              {freePlan?.price}
            </Typography>
          </Box>

          <Button
            onClick={() => {
              if (isLoggedIn) navigate('/organization');
              else navigate('/sign-up');
            }}
            variant="outlined"
            fullWidth
            sx={{
              backgroundColor: 'var(--color-bg-secondary)',
              mb: 3,
              '&:hover': {
                backgroundColor: 'var(--color-action-hover)',
                borderColor: 'var(--color-primary)',
              },
            }}
          >
            {freePlan?.button_text}
          </Button>

          <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
            {Array.isArray(freePlan?.data) &&
              freePlan?.data?.map((feature, key) => (
                <li
                  key={key}
                  style={{
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={Tick_icon}
                    alt=""
                    style={{
                      flexShrink: 0,
                      width: '20px',
                      height: '20px',
                    }}
                  />
                  <span
                    style={{
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontWeight: '400',
                      color: 'var(--color-text-secondary)',
                      paddingLeft: '10px',
                      display: 'inline-block',
                    }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
          </ul>
        </Box>
        <Box
          sx={{
            borderRadius: '12px',
            padding: '32px 24px',
            backgroundColor: 'var(--color-primary)',
            boxShadow: 'none',
            maxWidth: '400px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '22px',
                fontWeight: '600',
                color: 'var(--color-primary-contrast)',
                pb: 1,
              }}
            >
              {customPlan?.name}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: '400',
              color: 'var(--color-primary-contrast)',
              pb: 1,
            }}
          >
            {customPlan?.description}
          </Typography>

          <Button
            onClick={() => {
              navigate('/contact-support');
              window.scrollTo({ top: 0, behavior: 'auto' });
            }}
            variant="outlined"
            fullWidth
            sx={{
              backgroundColor: 'var(--color-bg)',
              mb: 3,
              '&:hover': {
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-bg-secondary)',
              },
            }}
          >
            {customPlan?.button_text}
          </Button>

          <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
            {Array.isArray(customPlan?.data) &&
              customPlan?.data?.map((feature, key) => (
                <li
                  key={key}
                  style={{
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={Tick_icon}
                    alt=""
                    style={{
                      flexShrink: 0,
                      width: '20px',
                      height: '20px',
                    }}
                  />
                  <span
                    style={{
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontWeight: '400',
                      color: 'var(--color-primary-contrast)',
                      paddingLeft: '10px',
                      display: 'inline-block',
                    }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
          </ul>
        </Box>
      </Stack>
    </Box>
  );
};

export default PricingTab;
